import { useState, useCallback, useRef, useEffect } from "react";
import Axios from "axios";
import { environment } from "../../environment";
import { errorCatcher } from "../../hooks";
import { useLogin } from "../login";
import { IBrandFeatureFlag } from "./types";

export const useBrandFeatureFlags = () => {
    const [flags, setFlags] = useState<IBrandFeatureFlag[]>([]);
    const [loading, setLoading] = useState(false);
    const [, authToken, loginFunctions] = useLogin();
    const loginFunctionsRef = useRef(loginFunctions);

    useEffect(() => {
        loginFunctionsRef.current = loginFunctions;
    }, [loginFunctions]);

    const listFlags = useCallback(async (brandId: number) => {
        setLoading(true);
        const result = await Axios.get(`${environment.brandsUrl}/${brandId}/feature-flags`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        if (result) {
            setFlags(result.data);
        }
        setLoading(false);
    }, [authToken]);

    const updateFlag = useCallback(async (brandId: number, flagName: string, enabled: boolean) => {
        const result = await Axios.put(
            `${environment.brandsUrl}/${brandId}/feature-flags`,
            { flag_name: flagName, enabled },
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        if (result) {
            setFlags(prev =>
                prev.map(f => f.flag_name === flagName ? { ...f, enabled } : f)
            );
        }
    }, [authToken]);

    const isFlagEnabled = useCallback((flagName: string, defaultWhenMissing = true): boolean => {
        const flag = flags.find(f => f.flag_name === flagName);
        return flag ? flag.enabled : defaultWhenMissing; // most flags default ON; pass false for opt-in flags
    }, [flags]);

    return { flags, loading, listFlags, updateFlag, isFlagEnabled };
};
